import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { UserPicker } from '../../components/UserPicker.js';

interface ExecutionRow {
  readonly auditExecutionId: string;
  readonly executionCode: string;
  readonly areaAudited: string;
  readonly auditorName: string | null;
  readonly auditorCode: string | null;
  readonly findingsCount: number;
  readonly status: string;
}

export function M21AuditExecutionPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ExecutionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [auditPlanId, setAuditPlanId] = useState('');
  const [executionCode, setExecutionCode] = useState('');
  const [auditorId, setAuditorId] = useState('');
  const [areaAudited, setAreaAudited] = useState('');
  const [auditStartDate, setAuditStartDate] = useState('');

  function load(): void {
    apiRequest<{ data: ExecutionRow[] }>('/v1/audit/executions', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: ExecutionRow[] }>('/v1/audit/executions', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!auditPlanId.trim() || !executionCode.trim() || !auditorId.trim()
      || !areaAudited.trim() || !auditStartDate) {
      setActionError(t('m21.auditExecution.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/audit/executions', {
        method: 'POST',
        token,
        body: {
          auditPlanId: auditPlanId.trim(),
          executionCode: executionCode.trim(),
          auditorId: auditorId.trim(),
          areaAudited: areaAudited.trim(),
          auditStartDate,
        },
      });
      setAuditPlanId(''); setExecutionCode(''); setAuditorId('');
      setAreaAudited(''); setAuditStartDate('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function closeExecution(id: string): Promise<void> {
    setActionError(null);
    setBusyId(`close-${id}`);
    try {
      await apiRequest(`/v1/audit/executions/${id}/close`, { method: 'POST', token, body: {} });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.auditExecution.loading')}</p>;
  return (
    <main aria-label="audit-execution">
      <h1>{t('m21.auditExecution.title')}</h1>

      <form aria-label="audit-execution-create-form" onSubmit={handleCreate}>
        <RecordPicker<{ auditPlanId: string; planCode: string; planName: string }>
          endpoint="/v1/audit/plans"
          idField="auditPlanId"
          label={t('m21.auditExecution.formPlan')}
          testId="audit-plan"
          value={auditPlanId}
          optionLabel={(p) => `${p.planCode} \u2014 ${p.planName}`}
          onPick={(id) => setAuditPlanId(id)}
        />
        <label>{t('m21.auditExecution.formCode')}<input aria-label="Execution code" value={executionCode} onChange={(e) => setExecutionCode(e.target.value)} /></label>
        <UserPicker label={t('m21.auditExecution.formAuditor')} testId="auditor" value={auditorId} onPick={(id) => setAuditorId(id)} />
        <label>{t('m21.auditExecution.formArea')}<input aria-label="Area audited" value={areaAudited} onChange={(e) => setAreaAudited(e.target.value)} /></label>
        <label>{t('m21.auditExecution.formStart')}<DateInput aria-label="Audit start date" type="date" value={auditStartDate} onValueChange={(nextValue) => setAuditStartDate(nextValue)} /></label>
        <button type="submit" disabled={busyId === 'create'}>{t('m21.auditExecution.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="audit-executions-table">
        <thead><tr><th>{t('m21.auditExecution.colCode')}</th><th>{t('m21.auditExecution.colArea')}</th><th>{t('m21.auditExecution.colAuditor')}</th><th>{t('m21.auditExecution.colFindings')}</th><th>{t('m21.auditExecution.colStatus')}</th><th>{t('m21.auditExecution.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.auditExecutionId}>
              <td>{e.executionCode}</td>
              <td>{e.areaAudited}</td>
              <td>{[e.auditorCode, e.auditorName].filter(Boolean).join(' \u2014 ')}</td>
              <td>{e.findingsCount}</td>
              <td>{e.status}</td>
              <td>
                {e.status.toUpperCase() !== 'COMPLETED' && (
                  <button
                    type="button"
                    aria-label={`close-${e.auditExecutionId}`}
                    disabled={busyId === `close-${e.auditExecutionId}`}
                    onClick={() => closeExecution(e.auditExecutionId)}
                  >{t('m21.auditExecution.close')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
